/**
 * Plugins-toggle feature — Ducks pattern (actions, reducer, types).
 * Manages which plugins are enabled/disabled.
 */

import { PluginInfo, PluginManifest } from '@/infra/types/domain';

// ─── State Shape ───────────────────────────────────────────

export interface PluginsToggleState {
  plugins: PluginInfo[];
  loading: boolean;
  error: string | null;
}

export const initialState: PluginsToggleState = {
  plugins: [],
  loading: false,
  error: null,
};

// ─── Action Types ──────────────────────────────────────────

const SET_PLUGINS = 'plugins-toggle/setPlugins';
const ENABLE_PLUGIN = 'plugins-toggle/enablePlugin';
const DISABLE_PLUGIN = 'plugins-toggle/disablePlugin';
export const REPLACE_PLUGIN_MANIFEST = 'plugins-toggle/replacePluginManifest';
const SET_LOADING = 'plugins-toggle/setPluginsLoading';
const SET_ERROR = 'plugins-toggle/setPluginsError';

// Trigger actions
export const FETCH_PLUGINS = 'plugins-toggle/fetchPlugins';
export const TOGGLE_PLUGIN = 'plugins-toggle/togglePlugin';

// ─── Action Creators ───────────────────────────────────────

export function setPlugins(plugins: PluginInfo[]) {
  return { type: SET_PLUGINS, payload: plugins } as const;
}

export function enablePlugin(id: string) {
  return { type: ENABLE_PLUGIN, payload: id } as const;
}

export function disablePlugin(id: string) {
  return { type: DISABLE_PLUGIN, payload: id } as const;
}

// Pext-style server-truth replay: dispatched by togglePluginSaga with the
// PluginManifest returned by EnablePlugin / DisablePlugin. Upserts the
// PluginInfo row by id so description, icon, display_name, todo_types, and
// enabled all converge on server state in one shot — no refetch needed.
export function replacePluginManifest(manifest: PluginManifest) {
  return { type: REPLACE_PLUGIN_MANIFEST, payload: manifest } as const;
}

export function setPluginsLoading(loading: boolean) {
  return { type: SET_LOADING, payload: loading } as const;
}

export function setPluginsError(error: string | null) {
  return { type: SET_ERROR, payload: error } as const;
}

export function fetchPlugins() {
  return { type: FETCH_PLUGINS } as const;
}

export function togglePlugin(id: string, enabled: boolean) {
  return { type: TOGGLE_PLUGIN, payload: { id, enabled } } as const;
}

// ─── Action Type Union ─────────────────────────────────────

type PluginsToggleAction =
  | ReturnType<typeof setPlugins>
  | ReturnType<typeof enablePlugin>
  | ReturnType<typeof disablePlugin>
  | ReturnType<typeof replacePluginManifest>
  | ReturnType<typeof setPluginsLoading>
  | ReturnType<typeof setPluginsError>;

// ─── Reducer ───────────────────────────────────────────────

export function pluginsToggleReducer(
  state: PluginsToggleState = initialState,
  action: PluginsToggleAction | { type: string; payload?: unknown }
): PluginsToggleState {
  switch (action.type) {
    case SET_PLUGINS: {
      const plugins = (action as ReturnType<typeof setPlugins>).payload;
      return { ...state, plugins };
    }

    case ENABLE_PLUGIN: {
      const id = (action as ReturnType<typeof enablePlugin>).payload;
      return {
        ...state,
        plugins: state.plugins.map((p) =>
          p.id === id ? { ...p, enabled: true } : p
        ),
      };
    }

    case DISABLE_PLUGIN: {
      const id = (action as ReturnType<typeof disablePlugin>).payload;
      return {
        ...state,
        plugins: state.plugins.map((p) =>
          p.id === id ? { ...p, enabled: false } : p
        ),
      };
    }

    case REPLACE_PLUGIN_MANIFEST: {
      // Server-truth replay: project the manifest onto the PluginInfo row
      // (the slice's narrower view-model). Full replace by id — no merge,
      // matching replaceTodo's semantics.
      const manifest = (action as ReturnType<typeof replacePluginManifest>).payload;
      const next: PluginInfo = {
        id: manifest.id,
        display_name: manifest.display_name,
        icon: manifest.icon,
        enabled: manifest.enabled ?? false,
        description: manifest.description,
        todo_types: manifest.todo_types,
      };
      const idx = state.plugins.findIndex((p) => p.id === manifest.id);
      if (idx === -1) {
        return { ...state, plugins: [...state.plugins, next] };
      }
      const plugins = state.plugins.slice();
      plugins[idx] = next;
      return { ...state, plugins };
    }

    case SET_LOADING: {
      const loading = (action as ReturnType<typeof setPluginsLoading>).payload;
      return { ...state, loading };
    }

    case SET_ERROR: {
      const error = (action as ReturnType<typeof setPluginsError>).payload;
      return { ...state, error };
    }

    default:
      return state;
  }
}
